import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material';

import {
  useGetContractByAgentQuery,
  useUpdateContractAgentMutation,
} from '@/apis/extended/contractApi';
import { CommonActions } from '@/components/Common/CommonContract/CommonActions';
import { CommonPrevNextButtons } from '@/components/Common/CommonContract/CommonPrevNextButtons';
import { CommonFormControl } from '@/components/Common/CommonFormControl';
import { CommonNumberInput } from '@/components/Common/CommonNumberInput';
import { parseString } from '@/utils/helper';

const initialForm = {
  name: '',
  sellerPhoneNumber: 0,
  sellerAgentEmail: '',
  license: '',
  brokerage: '',
  brokerageLicense: '',
  commission: '',
  street: '',
  city: '',
  state: '',
  zip: '',
  agentLicenseNumber: '',
  agentCompanyName: '',
  agentCompanyLicense: '',
  agentCompanyStateLicense: '',
  agentCompanyAddress: '',
};

type ContractDetailAgentInfoProps = {
  uid: string;
  onPrev?: () => void;
  onNext?: () => void;
  sellerPhoneNumber?: number;
  sellerAgentEmail?: string;
};

export const ContractDetailAgentInfo = ({
  uid,
  onPrev,
  onNext,
}: ContractDetailAgentInfoProps) => {
  const { data: contract } = useGetContractByAgentQuery(uid);
  const [updateContractAgent, { isLoading }] = useUpdateContractAgentMutation();

  const [form, setForm] = useState(initialForm);

  useEffect(() => {
    const agentInfo = contract?.agentInfo;
    // Get agent from product if assigned by admin — not a field on the backend Product model; kept as-is (types-only pass).
    const productAgent = (contract?.product as any)?.agent;

    // Priority: existing agentInfo > product.agent (from admin assignment) > empty
    setForm({
      name: agentInfo?.name || productAgent?.name || '',
      sellerPhoneNumber: agentInfo?.sellerPhoneNumber || 0,
      sellerAgentEmail:
        agentInfo?.sellerAgentEmail || productAgent?.email || '',
      license: agentInfo?.license || '',
      brokerage: agentInfo?.brokerage || '',
      brokerageLicense: agentInfo?.brokerageLicense || '',
      commission: agentInfo?.commission
        ? parseString(agentInfo.commission)
        : contract?.product?.loanInfo?.commission
        ? parseString(contract.product.loanInfo.commission)
        : '',
      street: agentInfo?.street || '',
      city: agentInfo?.city || '',
      state: agentInfo?.state || '',
      zip: agentInfo?.zip || '',
      agentLicenseNumber: agentInfo?.agentLicenseNumber || '',
      agentCompanyName: agentInfo?.agentCompanyName || '',
      agentCompanyLicense: agentInfo?.agentCompanyLicense || '',
      agentCompanyStateLicense: agentInfo?.agentCompanyStateLicense || '',
      agentCompanyAddress: agentInfo?.agentCompanyAddress || '',
    });
  }, [contract]);

  const isValid = useMemo(() => {
    return (
      form.name &&
      form.sellerPhoneNumber &&
      form.sellerAgentEmail &&
      form.license &&
      form.brokerage &&
      form.brokerageLicense &&
      !isNaN(parseFloat(form.commission)) &&
      form.street &&
      form.city &&
      form.state &&
      form.zip &&
      form.agentLicenseNumber &&
      form.agentCompanyName &&
      form.agentCompanyLicense &&
      form.agentCompanyStateLicense &&
      form.agentCompanyAddress
    );
  }, [form]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    if (!isValid) return;
    await updateContractAgent({
      uid,
      agentInfo: {
        ...form,
        sellerPhoneNumber: Number(form.sellerPhoneNumber),
        sellerAgentEmail: form.sellerAgentEmail,
        commission: parseFloat(form.commission),
      },
    }).unwrap();
    onNext?.();
  };

  return (
    <Box>
      <Card sx={{ mt: 2 }}>
        <CardHeader
          title="Agent Information"
          action={
            <CommonActions
              isLoading={isLoading}
              isNextDisabled={!isValid}
              onPrev={onPrev}
              onNext={handleSubmit}
            />
          }
          avatar={<i className="fa-light fa-user" />}
        />
        <CardContent>
          <CommonFormControl label="Agent Name:" labelWidth={250}>
            <TextField
              placeholder="Agent Name"
              name="name"
              value={form.name}
              onChange={handleInputChange}
            />
          </CommonFormControl>
          <CommonFormControl
            label="Agent Phone:"
            labelWidth={250}
            sx={{ mt: 2 }}
          >
            <TextField
              type="tel"
              placeholder="+1 (XXX) XXX-XXXX"
              name="sellerPhoneNumber"
              value={form.sellerPhoneNumber}
              onChange={handleInputChange}
            />
          </CommonFormControl>
          <CommonFormControl
            label="Agent Email:"
            labelWidth={250}
            sx={{ mt: 2 }}
          >
            <TextField
              placeholder="Agent Email"
              name="sellerAgentEmail"
              value={form.sellerAgentEmail}
              onChange={handleInputChange}
            />
          </CommonFormControl>
          <CommonFormControl
            label="Agent License:"
            labelWidth={250}
            sx={{ mt: 2 }}
          >
            <TextField
              placeholder="Agent License"
              name="license"
              value={form.license}
              onChange={handleInputChange}
            />
          </CommonFormControl>
          <CommonFormControl
            label="Agent Brokerage:"
            labelWidth={250}
            sx={{ mt: 2 }}
          >
            <TextField
              placeholder="Agent Brokerage"
              name="brokerage"
              value={form.brokerage}
              onChange={handleInputChange}
            />
          </CommonFormControl>
          <CommonFormControl
            label="Agent Brokerage License:"
            labelWidth={250}
            sx={{ mt: 2 }}
          >
            <TextField
              placeholder="Agent Brokerage License"
              name="brokerageLicense"
              value={form.brokerageLicense}
              onChange={handleInputChange}
            />
          </CommonFormControl>
          <CommonFormControl
            label="Agent Commission:"
            labelWidth={250}
            sx={{ mt: 2 }}
          >
            <TextField
              type="number"
              placeholder="Agent Commission %"
              name="commission"
              value={form.commission}
              onChange={handleInputChange}
            />
          </CommonFormControl>

          <CommonFormControl
            label="Street Address:"
            labelWidth={250}
            sx={{ mt: 2 }}
          >
            <TextField
              placeholder="Street Address"
              name="street"
              value={form.street}
              onChange={handleInputChange}
            />
          </CommonFormControl>

          <CommonFormControl label="City:" labelWidth={250} sx={{ mt: 2 }}>
            <TextField
              placeholder="City"
              name="city"
              value={form.city}
              onChange={handleInputChange}
            />
          </CommonFormControl>

          <CommonFormControl label="State:" labelWidth={250} sx={{ mt: 2 }}>
            <Select
              name="state"
              value={form.state}
              onChange={handleSelectChange}
            >
              <MenuItem value="">Select State</MenuItem>
              <MenuItem value="AL">Alabama</MenuItem>
              <MenuItem value="AK">Alaska</MenuItem>
              <MenuItem value="AZ">Arizona</MenuItem>
              <MenuItem value="AR">Arkansas</MenuItem>
              <MenuItem value="CA">California</MenuItem>
              <MenuItem value="CO">Colorado</MenuItem>
              <MenuItem value="CT">Connecticut</MenuItem>
              <MenuItem value="DE">Delaware</MenuItem>
              <MenuItem value="FL">Florida</MenuItem>
              <MenuItem value="GA">Georgia</MenuItem>
              <MenuItem value="HI">Hawaii</MenuItem>
              <MenuItem value="ID">Idaho</MenuItem>
              <MenuItem value="IL">Illinois</MenuItem>
              <MenuItem value="IN">Indiana</MenuItem>
              <MenuItem value="IA">Iowa</MenuItem>
              <MenuItem value="KS">Kansas</MenuItem>
              <MenuItem value="KY">Kentucky</MenuItem>
              <MenuItem value="LA">Louisiana</MenuItem>
              <MenuItem value="ME">Maine</MenuItem>
              <MenuItem value="MD">Maryland</MenuItem>
              <MenuItem value="MA">Massachusetts</MenuItem>
              <MenuItem value="MI">Michigan</MenuItem>
              <MenuItem value="MN">Minnesota</MenuItem>
              <MenuItem value="MS">Mississippi</MenuItem>
              <MenuItem value="MO">Missouri</MenuItem>
              <MenuItem value="MT">Montana</MenuItem>
              <MenuItem value="NE">Nebraska</MenuItem>
              <MenuItem value="NV">Nevada</MenuItem>
              <MenuItem value="NH">New Hampshire</MenuItem>
              <MenuItem value="NJ">New Jersey</MenuItem>
              <MenuItem value="NM">New Mexico</MenuItem>
              <MenuItem value="NY">New York</MenuItem>
              <MenuItem value="NC">North Carolina</MenuItem>
              <MenuItem value="ND">North Dakota</MenuItem>
              <MenuItem value="OH">Ohio</MenuItem>
              <MenuItem value="OK">Oklahoma</MenuItem>
              <MenuItem value="OR">Oregon</MenuItem>
              <MenuItem value="PA">Pennsylvania</MenuItem>
              <MenuItem value="RI">Rhode Island</MenuItem>
              <MenuItem value="SC">South Carolina</MenuItem>
              <MenuItem value="SD">South Dakota</MenuItem>
              <MenuItem value="TN">Tennessee</MenuItem>
              <MenuItem value="TX">Texas</MenuItem>
              <MenuItem value="UT">Utah</MenuItem>
              <MenuItem value="VT">Vermont</MenuItem>
              <MenuItem value="VA">Virginia</MenuItem>
              <MenuItem value="WA">Washington</MenuItem>
              <MenuItem value="WV">West Virginia</MenuItem>
              <MenuItem value="WI">Wisconsin</MenuItem>
              <MenuItem value="WY">Wyoming</MenuItem>
            </Select>
          </CommonFormControl>

          <CommonFormControl label="ZIP Code:" labelWidth={250} sx={{ mt: 2 }}>
            <TextField
              placeholder="ZIP Code"
              name="zip"
              value={form.zip}
              onChange={handleInputChange}
            />
          </CommonFormControl>

          <CommonFormControl
            label="Agent License Number:"
            labelWidth={250}
            sx={{ mt: 2 }}
          >
            <TextField
              placeholder="Agent License Number"
              name="agentLicenseNumber"
              value={form.agentLicenseNumber}
              onChange={handleInputChange}
            />
          </CommonFormControl>

          <CommonFormControl
            label="Company Name:"
            labelWidth={250}
            sx={{ mt: 2 }}
          >
            <TextField
              placeholder="Agent Company Name"
              name="agentCompanyName"
              value={form.agentCompanyName}
              onChange={handleInputChange}
            />
          </CommonFormControl>

          <CommonFormControl
            label="Company License:"
            labelWidth={250}
            sx={{ mt: 2 }}
          >
            <TextField
              placeholder="Agent Company License"
              name="agentCompanyLicense"
              value={form.agentCompanyLicense}
              onChange={handleInputChange}
            />
          </CommonFormControl>

          <CommonFormControl
            label="Company State License:"
            labelWidth={250}
            sx={{ mt: 2 }}
          >
            <TextField
              placeholder="Agent Company State License"
              name="agentCompanyStateLicense"
              value={form.agentCompanyStateLicense}
              onChange={handleInputChange}
            />
          </CommonFormControl>

          <CommonFormControl
            label="Company Address:"
            labelWidth={250}
            sx={{ mt: 2 }}
          >
            <TextField
              placeholder="Agent Company Address"
              name="agentCompanyAddress"
              value={form.agentCompanyAddress}
              onChange={handleInputChange}
            />
          </CommonFormControl>
        </CardContent>
      </Card>
      <CommonPrevNextButtons
        sx={{ mt: 3 }}
        isLoading={isLoading}
        isNextDisabled={!isValid}
        onPrev={onPrev}
        onNext={handleSubmit}
      />
    </Box>
  );
};
